// EditClinicStaffModal — manage a clinic mailbox's named staff (sub-identities).
// The mailbox has no personal PIN; its people do, so this is where an admin
// resets a person's PIN, removes them, re-activates, or adds someone directly.
// Supervisor tier and up (the calling row is gated in AdminPanel).

import { useEffect, useState, useCallback } from 'react';
import { adminGetClinicStaff, adminClinicStaffAction } from '../api/client.js';
import { CLINIC_DEFAULT_ROLE } from '../lib/clinicFunctions.js';

const backdrop = {
  position: 'fixed', inset: 0, zIndex: 6000, display: 'flex',
  alignItems: 'center', justifyContent: 'center', padding: 16,
  background: 'rgba(20, 30, 45, 0.45)',
  backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
};
const card = {
  width: '100%', maxWidth: 480, maxHeight: '85vh', overflowY: 'auto',
  background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16,
  padding: 24, boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
};
const STATUS_LABEL = { active: 'Active', pending: 'Awaiting approval', disabled: 'Removed', declined: 'Declined' };

export default function EditClinicStaffModal({ email, onClose }) {
  const [data, setData] = useState(null);   // { clinicName, roster }
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [fullName, setFullName] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);   // subId pending a delete confirm

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await adminGetClinicStaff(email)); }
    catch (e) { setError(e.message || 'Could not load staff.'); }
    finally { setLoading(false); }
  }, [email]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function act(subId, action) {
    setBusyId(`${action}:${subId}`); setError(null);
    try { await adminClinicStaffAction(email, action, { subId }); setConfirmDelete(null); await load(); }
    catch (e) { setError(e.message || 'Action failed.'); }
    finally { setBusyId(null); }
  }
  async function add(e) {
    e.preventDefault();
    if (!fullName.trim()) return;
    setBusyId('add'); setError(null);
    try {
      await adminClinicStaffAction(email, 'add', { fullName: fullName.trim(), jobTitle: CLINIC_DEFAULT_ROLE });
      setFullName(''); setAdding(false); await load();
    } catch (e2) { setError(e2.message || 'Could not add.'); }
    finally { setBusyId(null); }
  }

  const roster = data?.roster || [];

  return (
    <div style={backdrop} role="dialog" aria-modal="true" aria-label="Edit clinic staff"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Clinic staff</h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-3)' }}>{data?.clinicName || email}</p>
          </div>
          <button className="btn ghost sm" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {error && <div role="alert" className="callout danger" style={{ marginTop: 14, fontSize: 12, padding: '8px 10px' }}><div style={{ flex: 1 }}>{error}</div></div>}

        {loading ? (
          <p style={{ color: 'var(--text-3)', marginTop: 16 }}>Loading…</p>
        ) : (
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {roster.length === 0 && <p style={{ color: 'var(--text-3)', fontSize: 13 }}>No one is registered at this clinic yet.</p>}
            {roster.map(r => (
              <div key={r.subId} style={{
                display: 'flex', flexDirection: 'column', gap: 10, padding: '10px 12px',
                border: '1px solid var(--border)', borderRadius: 10,
                opacity: (r.status === 'disabled' || r.status === 'declined') ? 0.6 : 1,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{r.displayName}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 1 }}>
                      {[r.jobTitle, STATUS_LABEL[r.status] || r.status, r.status === 'active' ? (r.hasPin ? 'PIN set' : 'no PIN yet') : null].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  {confirmDelete !== r.subId && (
                    <>
                      {r.status === 'active' && r.hasPin && (
                        <button className="btn ghost sm" disabled={busyId === `reset:${r.subId}`} onClick={() => act(r.subId, 'reset')}>
                          {busyId === `reset:${r.subId}` ? '…' : 'Reset PIN'}
                        </button>
                      )}
                      {(r.status === 'disabled' || r.status === 'declined')
                        ? <button className="btn ghost sm" disabled={busyId === `activate:${r.subId}`} onClick={() => act(r.subId, 'activate')}>{r.status === 'declined' ? 'Approve' : 'Re-activate'}</button>
                        : <button className="btn ghost sm" disabled={busyId === `remove:${r.subId}`} onClick={() => act(r.subId, 'remove')}>
                            {busyId === `remove:${r.subId}` ? '…' : 'Remove'}
                          </button>}
                      <button className="btn danger filled sm" onClick={() => { setError(null); setConfirmDelete(r.subId); }} title="Permanently delete">Delete</button>
                    </>
                  )}
                </div>

                {confirmDelete === r.subId && (
                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10 }}>
                    <div style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.5 }}>
                      Permanently delete <strong>{r.displayName}</strong>? This removes their name, PIN, sessions and case shares for good. Cases they already reported are kept. This cannot be undone.
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                      <button className="btn danger filled sm" disabled={busyId === `delete:${r.subId}`} onClick={() => act(r.subId, 'delete')}>
                        {busyId === `delete:${r.subId}` ? 'Deleting…' : 'Delete permanently'}
                      </button>
                      <button className="btn ghost sm" disabled={busyId === `delete:${r.subId}`} onClick={() => setConfirmDelete(null)}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div style={{ borderTop: '1px solid var(--border)', marginTop: 16, paddingTop: 14 }}>
          {adding ? (
            <form onSubmit={add} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <input className="input" placeholder="Full name" value={fullName} onChange={e => setFullName(e.target.value)} autoFocus maxLength={200} />
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className="btn primary sm" disabled={!fullName.trim() || busyId === 'add'}>{busyId === 'add' ? 'Adding…' : 'Add staff member'}</button>
                <button type="button" className="btn ghost sm" onClick={() => { setAdding(false); setFullName(''); }}>Cancel</button>
              </div>
            </form>
          ) : (
            <button className="btn ghost sm" onClick={() => setAdding(true)}>＋ Add staff member</button>
          )}
        </div>
      </div>
    </div>
  );
}
